import Toggle from 'components/Toggle/Toggle';
import CustomSelect from 'components/Select/FormikSelect';
import { FormikContextType, useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';
import { IQuestion, QuestionType } from 'types/Question.types';
import { capitalizeFirstLetter } from 'utils/stringUtils';
import { DropdownType } from './constants';
import {
  getComponentsBasedOnDropdownType,
  getDropdownTypeFromListQuestion,
  getSelectOptionsFromQuestion,
  inlineControlStacksWhenNarrow,
  isInlineControlQuestion,
  questionHasValue,
} from './helpers';
import OnBlurInputFormikText from 'components/Input/OnBlurInputFormik/OnBlurInputFormikText';
import OnBlurInputFormikNumber from 'components/Input/OnBlurInputFormik/OnBlurInputFormikNumber';
import DisabledDropdownIndicator from 'components/Select/components/DisabledDropdownIndicator/DisabledDropdownIndicator';
import { useIsTouched } from 'utils/hooks/useIsTouched';
import { setTouchedQuestionName } from 'store/Form/actions/form';
import RadioInput from 'components/Input/RadioInput/RadioInput';
import TableInput from 'components/Input/TableInput/TableInput';
import ColorInput from 'components/Input/ColorInput/ColorInput';
import {
  BooleanQuestionWrapper,
  StyledField,
  ColorInputWrapper,
} from './FormItem.styled';
import FormItemWrapper from '../FormItemWrapper/FormItemWrapper';
import { useTranslation } from 'react-i18next';
import { IOption } from 'components/Select/type';
import { useSetManuallyChangeQuestionName } from '../hooks';
import { useAllowIncompleteAnswers } from 'utils/hooks/useTemplateConfigureParams';

interface IFormItemProps {
  question: IQuestion;
  hasImages: boolean;
  questionPrice: number | undefined;
  // Set while the question has a value to clear.
  showClear?: boolean;
  // Set on every clearable question, answered or not: the control keeps the
  // button's space reserved so nothing moves when it appears.
  reserveClear?: boolean;
  // False when the answer cannot be emptied at all, so a control that carries
  // a clear of its own drops it too.
  allowClear?: boolean;
  onClear?: () => void;
}

const FormItem = ({
  question,
  hasImages,
  questionPrice,
  showClear = false,
  reserveClear = false,
  allowClear = true,
  onClear,
}: IFormItemProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const context: FormikContextType<any> = useFormikContext();
  const isTouched = useIsTouched(question.initialKey);
  const setManuallyChangeQuestionName =
    useSetManuallyChangeQuestionName(dispatch);
  const allowIncompleteAnswers = useAllowIncompleteAnswers();
  const clearProps = { showClear, reserveClear, onClear };

  return (
    <FormItemWrapper
      question={question}
      context={context}
      isTouched={isTouched}
      questionPrice={questionPrice}
      isInlineControl={isInlineControlQuestion(question.type)}
      stacksWhenNarrow={inlineControlStacksWhenNarrow(question.type)}
    >
      {(() => {
        switch (question.type) {
          case QuestionType.NUMBER:
            return (
              <OnBlurInputFormikNumber
                pwId={'FormItem_' + question.initialKey}
                question={question}
                context={context}
                isTouched={isTouched}
                {...clearProps}
              />
            );
          case QuestionType.BOOLEAN: {
            const isChecked = context.values[question.initialKey];
            return (
              <BooleanQuestionWrapper hasImages={hasImages}>
                <Toggle
                  pwId={'FormItem_' + question.initialKey}
                  isChecked={isChecked}
                  questionKey={question.initialKey}
                  onClick={() => {
                    const newValues = { ...context.values };
                    newValues[question.initialKey] = !isChecked;
                    context.setValues(newValues);
                  }}
                />
              </BooleanQuestionWrapper>
            );
          }
          case QuestionType.LIST: {
            const dropdownType: DropdownType =
              getDropdownTypeFromListQuestion(question);
            const baseComponents =
              getComponentsBasedOnDropdownType(dropdownType);
            const selectComponents = question.disabled
              ? {
                  ...baseComponents,
                  DropdownIndicator: DisabledDropdownIndicator,
                }
              : baseComponents;

            return (
              <StyledField
                pwId={'FormItem_' + question.initialKey}
                height={'40rem'}
                width={'100%'}
                styles={{
                  menuPortal: { width: '100%' },
                  width: '100%',
                }}
                name={question.initialKey}
                options={getSelectOptionsFromQuestion(question)}
                component={CustomSelect}
                translate={false}
                onSelect={(option: IOption | IOption[]) => {
                  setManuallyChangeQuestionName(option, question.initialKey);
                }}
                placeholder={capitalizeFirstLetter(question.name)}
                isMulti={question.multiple}
                isClearable={allowClear ? undefined : false}
                isError={isTouched && !!context.errors[question.initialKey]}
                disabled={!!question.disabled}
                components={selectComponents}
                validate={(value: string) => {
                  if (
                    question.required &&
                    !allowIncompleteAnswers &&
                    !questionHasValue(value)
                  ) {
                    return t('{{FIELD}} is invalid').replace(
                      '{{FIELD}}',
                      question.name
                    );
                  }
                }}
                onMenuClose={() => {
                  if (!isTouched) {
                    dispatch(setTouchedQuestionName(question.initialKey));
                  }
                }}
                hasImages={hasImages}
                {...clearProps}
              />
            );
          }
          case QuestionType.TABLE:
            return (
              <TableInput
                pwId={'FormItem_' + question.initialKey}
                question={question}
                context={context}
                isTouched={isTouched}
                {...clearProps}
              />
            );
          case QuestionType.TEXT:
            return (
              <OnBlurInputFormikText
                pwId={'FormItem_' + question.initialKey}
                question={question}
                context={context}
                isTouched={isTouched}
                {...clearProps}
              />
            );
          case QuestionType.RADIO:
            return (
              <RadioInput
                pwId={'FormItem_' + question.initialKey}
                question={question}
                context={context}
                {...clearProps}
              />
            );
          case QuestionType.COLOR:
            return (
              <ColorInputWrapper hasImages={hasImages}>
                <ColorInput
                  pwId={'FormItem_' + question.initialKey}
                  question={question}
                  context={context}
                  isTouched={isTouched}
                  {...clearProps}
                />
              </ColorInputWrapper>
            );
          default:
            return null;
        }
      })()}
    </FormItemWrapper>
  );
};

export default FormItem;
